import type { TripType } from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/hooks/search-inputs-manager'
import {
    SEARCH_MODIFIER_POPPER_Z_INDEX,
    useSearchContainerPopperAnchor
} from '@/src/components/desktop/srp/searchHeader/searchModifierInputs/hooks/useSearchContainerPopperAnchor'
import { ExpandLess } from '@mui/icons-material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Box, Button, MenuItem, Paper, Popper } from '@mui/material'
import { useTranslations } from 'next-intl'
import { useRef } from 'react'

interface ITripTypeSelectorProps {
    tripType: TripType
    onTripTypeChange?: (tripType: TripType) => void
    active: boolean
    onActivate: () => void
    onClose: () => void
    /** Anchor dropdown flush to the search bar bottom instead of the trigger. */
    attachToSearchContainer?: boolean
}

export const TripTypeSelector: React.FC<ITripTypeSelectorProps> = ({
    tripType,
    onTripTypeChange,
    active,
    onActivate,
    onClose,
    attachToSearchContainer = false
}) => {
    const t = useTranslations('common')
    const buttonRef = useRef<HTMLButtonElement>(null)
    const { anchorEl, offset, popperOpen } = useSearchContainerPopperAnchor(
        active,
        buttonRef,
        attachToSearchContainer
    )

    const labelMap: Record<TripType, string> = {
        oneway: t('oneWay'),
        roundtrip: t('roundTrip'),
        multicity: t('multicity')
    }

    const handleButtonClick = () => {
        if (active) {
            onClose()
        } else {
            onActivate()
        }
    }

    const handleTripTypeSelect = (value: TripType) => {
        onTripTypeChange?.(value)
        onClose()
    }

    const options: TripType[] = ['oneway', 'roundtrip', 'multicity']

    return (
        <Box sx={{ pr: 0.5 }}>
            <Button
                ref={buttonRef}
                data-testid="triptype-button"
                variant="text"
                onClick={handleButtonClick}
                sx={{
                    minWidth: 'max-content',
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                        backgroundColor: 'transparent'
                    }
                }}
                endIcon={active ? <ExpandLess /> : <ExpandMoreIcon />}
            >
                {labelMap[tripType]}
            </Button>

            <Popper
                anchorEl={anchorEl}
                open={popperOpen}
                placement="bottom-start"
                modifiers={[{ name: 'offset', options: { offset } }]}
                style={{ zIndex: SEARCH_MODIFIER_POPPER_Z_INDEX }}
            >
                <Paper
                    elevation={3}
                    onMouseDown={e => e.stopPropagation()}
                    sx={{
                        minWidth: 160,
                        py: 0.5,
                        mt: attachToSearchContainer ? 0 : 1.5
                    }}
                >
                    {options.map(option => (
                        <MenuItem
                            key={option}
                            selected={tripType === option}
                            onClick={() => handleTripTypeSelect(option)}
                        >
                            {labelMap[option]}
                        </MenuItem>
                    ))}
                </Paper>
            </Popper>
        </Box>
    )
}
